import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowRight, PackageCheck, Ruler, Palette, MessageCircle, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";
import PageHero from "@/components/shared/PageHero";
import api from "@/lib/api";
import { useSite } from "@/contexts/SiteContext";

export default function ProductDetail() {
  const { slug } = useParams();
  const { settings } = useSite();
  const [product, setProduct] = useState(null);
  const [related, setRelated] = useState([]);
  const [activeImg, setActiveImg] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/products/${slug}`)
      .then((r) => {
        setProduct(r.data);
        setActiveImg(0);
        api.get("/products", { params: { category: r.data.category, limit: 4 } })
          .then((res) => setRelated((res.data.items || []).filter((p) => p.slug !== slug).slice(0, 4)))
          .catch(() => {});
      })
      .catch(() => setProduct(null))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) return <div className="min-h-[60vh] flex items-center justify-center text-foreground/60 tracking-widest uppercase text-xs">Loading…</div>;
  if (!product) return (
    <div className="py-40 text-center">
      <p className="section-label mb-3">404</p>
      <h2 className="serif-display text-4xl">Product not found</h2>
      <Link to="/products" className="mt-6 inline-block text-gold tracking-wider uppercase text-xs">Back to catalogue →</Link>
    </div>
  );

  const wa = settings.whatsapp?.replace(/[^0-9]/g, "") || "918799757375";
  const waMsg = encodeURIComponent(`Hi Hermanos, I'm interested in a wholesale enquiry for: ${product.name}. Please share catalogue and pricing.`);

  return (
    <div>
      <PageHero
        label={product.brand || "Wholesale Catalogue"}
        title={product.name}
        breadcrumbs={[{ label: "Products", to: "/products" }, { label: product.name }]}
      />

      <section className="py-14">
        <div className="mx-auto max-w-7xl px-4 md:px-8 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-7">
            <div className="aspect-[4/5] overflow-hidden rounded-md border border-border/60 bg-secondary">
              {product.images?.[activeImg] ? (
                <img src={product.images[activeImg]} alt={product.name} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-foreground/30 text-xs">No image</div>
              )}
            </div>
            {product.images?.length > 1 && (
              <div className="mt-4 grid grid-cols-5 gap-3">
                {product.images.map((im, i) => (
                  <button
                    key={i}
                    onClick={() => setActiveImg(i)}
                    className={`aspect-square overflow-hidden rounded-sm border ${activeImg === i ? "border-gold" : "border-border/60"}`}
                  >
                    <img src={im} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="lg:col-span-5">
            <p className="section-label mb-3">{product.brand || "Multi Brand"} • {product.category}</p>
            <h1 className="serif-display text-4xl md:text-5xl text-foreground">{product.name}</h1>
            <p className="mt-5 text-foreground/70 leading-relaxed">{product.description}</p>

            <div className="mt-6 grid grid-cols-2 gap-3 text-sm">
              <div className="p-4 rounded-md border border-border/60 bg-secondary/50">
                <p className="section-label text-[0.6rem]">MOQ</p>
                <p className="mt-1 font-serif text-2xl text-foreground">{product.moq}+ pcs</p>
              </div>
              <div className="p-4 rounded-md border border-border/60 bg-secondary/50">
                <p className="section-label text-[0.6rem]">Availability</p>
                <p className="mt-1 font-serif text-2xl text-gold">In Stock</p>
              </div>
            </div>

            {product.sizes?.length > 0 && (
              <div className="mt-6">
                <p className="section-label text-[0.62rem] mb-3 flex items-center gap-2"><Ruler size={13} /> Sizes Available</p>
                <div className="flex flex-wrap gap-2">
                  {product.sizes.map((s) => (
                    <span key={s} className="text-xs px-3 py-1.5 border border-border/60 rounded-sm bg-card">{s}</span>
                  ))}
                </div>
              </div>
            )}
            {product.colors?.length > 0 && (
              <div className="mt-5">
                <p className="section-label text-[0.62rem] mb-3 flex items-center gap-2"><Palette size={13} /> Colors</p>
                <div className="flex flex-wrap gap-2">
                  {product.colors.map((c) => (
                    <span key={c} className="text-xs px-3 py-1.5 border border-border/60 rounded-sm bg-card">{c}</span>
                  ))}
                </div>
              </div>
            )}

            {product.features?.length > 0 && (
              <div className="mt-6">
                <p className="section-label text-[0.62rem] mb-3">Features</p>
                <ul className="space-y-2 text-sm text-foreground/80">
                  {product.features.map((f, i) => (
                    <li key={i} className="flex gap-2"><PackageCheck size={15} className="text-gold shrink-0 mt-0.5" /> {f}</li>
                  ))}
                </ul>
              </div>
            )}

            <div className="mt-8 space-y-2">
              <Link to={`/request-quotation?product=${encodeURIComponent(product.name)}`}>
                <Button className="w-full rounded-sm h-12 bg-gold hover:bg-gold-400 text-navy-900 font-semibold tracking-wider uppercase text-xs">Request Quotation</Button>
              </Link>
              <Link to={`/request-catalogue?product=${encodeURIComponent(product.name)}`}>
                <Button variant="outline" className="w-full rounded-sm h-12 border-gold/40 hover:border-gold tracking-wider uppercase text-xs">Request Full Catalogue</Button>
              </Link>
              <div className="grid grid-cols-2 gap-2 pt-2">
                <a href={`https://wa.me/${wa}?text=${waMsg}`} target="_blank" rel="noreferrer" className="inline-flex items-center justify-center gap-2 h-11 rounded-sm border border-emerald_brand text-emerald-500 hover:bg-emerald_brand/10 text-xs tracking-wider uppercase">
                  <MessageCircle size={14} /> WhatsApp
                </a>
                <a href={`tel:${settings.phone_primary}`} className="inline-flex items-center justify-center gap-2 h-11 rounded-sm border border-border hover:border-gold text-foreground text-xs tracking-wider uppercase">
                  <Phone size={14} /> Call Sales
                </a>
              </div>
            </div>
            <p className="mt-6 text-xs text-foreground/50 leading-relaxed">
              This is a wholesale B2B catalogue. Pricing is shared on quotation only. Minimum order quantities apply.
            </p>
          </div>
        </div>
      </section>

      {related.length > 0 && (
        <section className="border-t border-border/60 py-16 bg-secondary/30">
          <div className="mx-auto max-w-7xl px-4 md:px-8">
            <p className="section-label mb-3"><span className="inline-block w-8 h-px bg-gold align-middle mr-3" />Related</p>
            <h2 className="serif-display text-3xl md:text-4xl text-foreground mb-8">More in {product.category}</h2>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
              {related.map((p) => (
                <Link key={p.id} to={`/products/${p.slug}`} className="group block relative overflow-hidden rounded-md border border-border/60 hover:border-gold/60 transition-colors bg-card">
                  <div className="aspect-[4/5] overflow-hidden">
                    {p.images?.[0] ? <img src={p.images[0]} alt={p.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-1000" loading="lazy" decoding="async" /> : null}
                  </div>
                  <div className="p-4">
                    <p className="section-label text-[0.6rem]">{p.brand}</p>
                    <p className="mt-1 font-serif text-base text-foreground group-hover:text-gold transition-colors line-clamp-1">{p.name}</p>
                    <p className="mt-2 text-xs text-foreground/60 flex items-center gap-1">MOQ {p.moq}+ pcs <ArrowRight size={11} className="ml-auto text-gold" /></p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
